import { useEffect, useLayoutEffect, useRef, useState } from "react";

const FLOATING_PANEL_WIDTH = 340;
const FLOATING_PANEL_MARGIN = 0;
const FLOATING_PANEL_TOP = 0;
const FLOATING_PANEL_BOTTOM = 0;
const FLOATING_FADE_DURATION_MS = 140;
const FLOATING_MOVE_DURATION_MS = 220;
const FLOATING_OPACITY_TRANSITION = "opacity 150ms ease-out";
const FLOATING_PANEL_HEIGHT_FALLBACK = 140;

let lastFloatingPosition = null;

function getDefaultFloatingPosition() {
    if (lastFloatingPosition) {
        return lastFloatingPosition;
    }

    if (typeof window === "undefined") {
        return { x: FLOATING_PANEL_MARGIN, y: FLOATING_PANEL_TOP };
    }

    return {
        x: FLOATING_PANEL_MARGIN,
        y: Math.max(
            FLOATING_PANEL_TOP,
            window.innerHeight - FLOATING_PANEL_HEIGHT_FALLBACK - FLOATING_PANEL_BOTTOM
        ),
    };
}

function getPanelDimensions(panelElement) {
    return {
        height: panelElement?.offsetHeight ?? FLOATING_PANEL_HEIGHT_FALLBACK,
        width: panelElement?.offsetWidth ?? FLOATING_PANEL_WIDTH,
    };
}

function clampFloatingPosition(position, panelElement) {
    if (typeof window === "undefined") {
        return position;
    }

    const { height, width } = getPanelDimensions(panelElement);
    const maxX = Math.max(FLOATING_PANEL_MARGIN, window.innerWidth - width);
    const maxY = Math.max(FLOATING_PANEL_TOP, window.innerHeight - height - FLOATING_PANEL_BOTTOM);

    return {
        x: Math.min(Math.max(position.x, FLOATING_PANEL_MARGIN), maxX),
        y: Math.min(Math.max(position.y, FLOATING_PANEL_TOP), maxY),
    };
}

function rememberFloatingPosition(position) {
    lastFloatingPosition = position;
}

export default function useFloatingPanel({
    dockedRect,
    isDockedRoute,
    isOpen,
}) {
    const [floatingPosition, setFloatingPosition] = useState(() => getDefaultFloatingPosition());
    const [renderPosition, setRenderPosition] = useState(() => getDefaultFloatingPosition());
    const [isRendered, setIsRendered] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [transform, setTransform] = useState("translate3d(0, 0, 0)");
    const [transition, setTransition] = useState(FLOATING_OPACITY_TRANSITION);

    const panelRef = useRef(null);
    const dragStateRef = useRef(null);
    const floatingPositionRef = useRef(floatingPosition);
    const pendingDragPositionRef = useRef(floatingPosition);
    const animationFrameRef = useRef(0);
    const previousIsDockedRouteRef = useRef(isDockedRoute);
    const lastDockedPositionRef = useRef(null);

    useEffect(() => {
        floatingPositionRef.current = floatingPosition;
        rememberFloatingPosition(floatingPosition);
    }, [floatingPosition]);

    useEffect(() => {
        function handleWindowResize() {
            const nextPosition = clampFloatingPosition(floatingPositionRef.current, panelRef.current);
            floatingPositionRef.current = nextPosition;
            pendingDragPositionRef.current = nextPosition;
            rememberFloatingPosition(nextPosition);
            setFloatingPosition(nextPosition);
            setRenderPosition(nextPosition);
            setTransform("translate3d(0, 0, 0)");
            setTransition(FLOATING_OPACITY_TRANSITION);
        }

        window.addEventListener("resize", handleWindowResize);

        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    useEffect(() => {
        if (!dockedRect) {
            return;
        }

        lastDockedPositionRef.current = {
            x: dockedRect.x,
            y: dockedRect.y,
        };
    }, [dockedRect]);

    useLayoutEffect(() => {
        let timeoutId = null;
        let firstAnimationFrameId = 0;
        let secondAnimationFrameId = 0;
        const cameFromDockedRoute = previousIsDockedRouteRef.current && !isDockedRoute;

        if (isOpen) {
            // Floating panel visibility/position is intentionally coordinated in layout effects for animation.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsRendered(true);
            setRenderPosition(floatingPositionRef.current);

            firstAnimationFrameId = window.requestAnimationFrame(() => {
                setIsVisible(true);

                if (cameFromDockedRoute && lastDockedPositionRef.current) {
                    const nextFloatingPosition = clampFloatingPosition(floatingPositionRef.current, panelRef.current);
                    const deltaX = lastDockedPositionRef.current.x - nextFloatingPosition.x;
                    const deltaY = lastDockedPositionRef.current.y - nextFloatingPosition.y;

                    floatingPositionRef.current = nextFloatingPosition;
                    pendingDragPositionRef.current = nextFloatingPosition;
                    rememberFloatingPosition(nextFloatingPosition);
                    setFloatingPosition(nextFloatingPosition);
                    setRenderPosition(nextFloatingPosition);
                    setTransition("none");
                    setTransform(`translate3d(${deltaX}px, ${deltaY}px, 0)`);

                    secondAnimationFrameId = window.requestAnimationFrame(() => {
                        setTransition(`transform ${FLOATING_MOVE_DURATION_MS}ms ease-out, ${FLOATING_OPACITY_TRANSITION}`);
                        setTransform("translate3d(0, 0, 0)");

                        timeoutId = window.setTimeout(() => {
                            setTransition(FLOATING_OPACITY_TRANSITION);
                        }, FLOATING_MOVE_DURATION_MS);
                    });

                    return;
                }

                setTransform("translate3d(0, 0, 0)");
                setTransition(FLOATING_OPACITY_TRANSITION);
            });
        } else {
            setIsVisible(false);
            setTransform("translate3d(0, 0, 0)");
            setTransition(FLOATING_OPACITY_TRANSITION);
            timeoutId = window.setTimeout(() => {
                setIsRendered(false);
            }, FLOATING_FADE_DURATION_MS);
        }

        return () => {
            window.cancelAnimationFrame(firstAnimationFrameId);
            window.cancelAnimationFrame(secondAnimationFrameId);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isDockedRoute, isOpen]);

    useEffect(() => {
        previousIsDockedRouteRef.current = isDockedRoute;
    }, [isDockedRoute]);

    useEffect(() => {
        return () => {
            dragStateRef.current = null;
            window.cancelAnimationFrame(animationFrameRef.current);
        };
    }, []);

    function queuePanelPositionUpdate(position) {
        floatingPositionRef.current = position;
        pendingDragPositionRef.current = position;
        rememberFloatingPosition(position);

        if (animationFrameRef.current) {
            return;
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = 0;
            setRenderPosition(pendingDragPositionRef.current);
        });
    }

    function handlePointerDown(event) {
        if (event.button !== 0 || event.target.closest("button")) {
            return;
        }

        setTransition(FLOATING_OPACITY_TRANSITION);
        setTransform("translate3d(0, 0, 0)");
        dragStateRef.current = {
            pointerId: event.pointerId,
            offsetX: event.clientX - floatingPositionRef.current.x,
            offsetY: event.clientY - floatingPositionRef.current.y,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    }

    function handlePointerMove(event) {
        const dragState = dragStateRef.current;

        if (!dragState || dragState.pointerId !== event.pointerId) {
            return;
        }

        queuePanelPositionUpdate(clampFloatingPosition({
            x: event.clientX - dragState.offsetX,
            y: event.clientY - dragState.offsetY,
        }, panelRef.current));
    }

    function handlePointerUp(event) {
        if (dragStateRef.current?.pointerId !== event.pointerId) {
            return;
        }

        dragStateRef.current = null;
        setFloatingPosition(floatingPositionRef.current);
        setRenderPosition(floatingPositionRef.current);
        event.currentTarget.releasePointerCapture(event.pointerId);
    }

    return {
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        isRendered,
        isVisible,
        panelRef,
        style: {
            left: renderPosition.x,
            top: renderPosition.y,
            transform,
            transition,
        },
    };
}
